import React, { useState, useCallback } from "react";
import Form from "react-bootstrap/Form";
import { getGeocode, getLatLng } from "use-places-autocomplete";
import axios from "axios";
import { ReactNotifications, Store } from "react-notifications-component";
import "react-notifications-component/dist/theme.css";
import "animate.css/animate.min.css";
import "./BasePointSearch.css";

const BasePointSearch = ({
  selectedPoint,
  onSelectPoint,
  renderExternalButton,
  onSearchValueChange,
  onSearchRef,
  autoSelectSingleResult = false,
  externalSearchValue,
  rightClickCoordinates,
  onClearRightClickCoordinates,
  onSetRightClickCoordinates,
  onSearchResultsChange,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Sincronizar el valor externo con el estado interno
  React.useEffect(() => {
    if (
      externalSearchValue !== undefined &&
      externalSearchValue !== searchValue
    ) {
      setSearchValue(externalSearchValue);
    }
  }, [externalSearchValue]);

  // Limpiar resultados de búsqueda cuando se selecciona un punto o hay coordenadas de clic derecho
  React.useEffect(() => {
    if ((selectedPoint || rightClickCoordinates) && searchResults.length > 0) {
      setSearchResults([]);
      if (onSearchResultsChange) {
        onSearchResultsChange([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoint, rightClickCoordinates]);

  const getFormattedAddress = async (lat, lon) => {
    if (lat && lon) {
      try {
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
        );
        return response.data.display_name;
      } catch (error) {
        console.error("Error fetching address:", error);
        return null;
      }
    }
    return null;
  };

  const handleSelectResult = useCallback(
    (location) => {
      // En lugar de seleccionar el punto (que dispara la búsqueda), establecer como punto temporal
      const coordinatesString = `${location.latitude}, ${location.longitude}`;

      if (onSetRightClickCoordinates) {
        onSetRightClickCoordinates({
          latitude: location.latitude,
          longitude: location.longitude,
          direccion: location.direccion || "",
          coordinatesString: coordinatesString,
        });
      }

      // Actualizar el valor del input interno
      setSearchValue(coordinatesString);

      // Solo limpiar los resultados de búsqueda, no el input
      setSearchResults([]);
      if (onSearchResultsChange) {
        onSearchResultsChange([]);
      }
    },
    [onSetRightClickCoordinates, onSearchResultsChange]
  );

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) return;
    // Antes de iniciar una nueva búsqueda, limpiar todo:
    if (onSelectPoint) onSelectPoint(null);
    if (onClearRightClickCoordinates) onClearRightClickCoordinates();
    setSearchResults([]);
    if (onSearchResultsChange) {
      onSearchResultsChange([]);
    }
    setSearchValue("");
    if (onSearchValueChange) onSearchValueChange("");

    setIsSearching(true);
    try {
      // Verificar si es una coordenada
      const coordinateRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
      const isCoordinate = coordinateRegex.test(searchValue.trim());

      if (isCoordinate) {
        // Es una coordenada
        const [latitude, longitude] = searchValue
          .split(",")
          .map((coord) => parseFloat(coord.trim()));

        if (
          !isNaN(latitude) &&
          !isNaN(longitude) &&
          latitude >= -90 &&
          latitude <= 90 &&
          longitude >= -180 &&
          longitude <= 180
        ) {
          const formattedAddress = await getFormattedAddress(
            latitude,
            longitude
          );
          const location = {
            latitude,
            longitude,
            direccion: formattedAddress || "",
            referencia: "",
          };

          setSearchResults([location]);
          if (onSearchResultsChange) {
            onSearchResultsChange([location]);
          }

          // Mostrar notificación de resultados encontrados después de que el estado se haya actualizado
          setTimeout(() => {
            addNotification(
              "success",
              "Resultados Encontrados",
              `Se ha encontrado 1 resultado de la búsqueda realizada.`,
              "top-right"
            );
          }, 100);

          // Si hay exactamente 1 resultado, seleccionarlo automáticamente
          handleSelectResult(location);
        } else {
          setSearchResults([]);
          setTimeout(() => {
            addNotification(
              "danger",
              "Ups!",
              "No se ha encontrado algún resultado. Favor de realizar nuevamente la búsqueda.",
              "top-right"
            );
          }, 100);
        }
      } else {
        // Es una búsqueda de dirección
        const results = await getGeocode({ address: searchValue });
        const locations = await Promise.all(
          results.map(async (result) => {
            const { lat, lng } = await getLatLng(result);
            const formattedAddress = result.formatted_address || "";
            return {
              latitude: lat,
              longitude: lng,
              direccion: formattedAddress,
              referencia: "",
            };
          })
        );
        setSearchResults(locations);
        if (onSearchResultsChange) {
          onSearchResultsChange(locations);
        }

        // Mostrar notificación de resultados encontrados después de que el estado se haya actualizado
        setTimeout(() => {
          addNotification(
            "success",
            "Resultados Encontrados",
            `Se ha${locations.length > 1 ? "n" : ""} encontrado ${
              locations.length
            } resultado${
              locations.length > 1 ? "s" : ""
            }  de la búsqueda realizada.`,
            "top-right"
          );
        }, 100);

        // Si hay exactamente 1 resultado, seleccionarlo automáticamente
        if (locations.length === 1) {
          handleSelectResult(locations[0]);
        }
      }
    } catch (error) {
      console.error("Error al buscar dirección:", error);
      setSearchResults([]);
      if (onSearchResultsChange) {
        onSearchResultsChange([]);
      }
      setTimeout(() => {
        addNotification(
          "danger",
          "Ups!",
          "No se ha encontrado algún resultado. Favor de realizar nuevamente la búsqueda.",
          "top-right"
        );
      }, 100);
    } finally {
      setIsSearching(false);
    }
  }, [
    searchValue,
    handleSelectResult,
    onSelectPoint,
    onClearRightClickCoordinates,
    onSearchValueChange,
  ]);

  // Exponer la función de búsqueda al componente padre
  React.useEffect(() => {
    if (onSearchRef) {
      onSearchRef.current = handleSearch;
    }
  }, [handleSearch, onSearchRef]);

  const addNotification = (
    notificationType,
    notificationTitle,
    notificationMessage,
    notificationPosition
  ) => {
    Store.addNotification({
      title: notificationTitle,
      message: notificationMessage,
      type: notificationType,
      insert: "top",
      container: notificationPosition,
      animationIn: ["animate__animated", "animate__bounceInRight"],
      animationOut: ["animate__animated", "animate__bounceOut"],
      dismiss: {
        duration: 5000,
        onScreen: true,
        pauseOnHover: true,
        waitForAnimation: true,
        showIcon: true,
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <ReactNotifications />
      <div className="base-point-search-container">
        <h6 className="fw-bold mb-3">Buscar y seleccionar punto base:</h6>
        <div className="base-point-search-wrapper">
          <Form.Group className="mb-3">
            {renderExternalButton ? (
              <div className="d-flex gap-2 align-items-center">
                <div className="flex-grow-1">
                  <div className="input-group">
                    <Form.Control
                      type="text"
                      placeholder="Escribe una coordenada, dirección, lugar o negocio"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        if (onSearchValueChange) {
                          onSearchValueChange(e.target.value);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={isSearching}
                    />
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={handleSearch}
                      disabled={isSearching || !searchValue.trim()}
                    >
                      <i className="fa fa-search"></i>
                    </button>
                  </div>
                </div>
                {renderExternalButton()}
              </div>
            ) : (
              <div className="input-group">
                <Form.Control
                  type="text"
                  placeholder="Escribe una coordenada, dirección, lugar o negocio"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    if (onSearchValueChange) {
                      onSearchValueChange(e.target.value);
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isSearching}
                />
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleSearch}
                  disabled={isSearching || !searchValue.trim()}
                >
                  <i className="fa fa-search"></i>
                </button>
              </div>
            )}
            {isSearching && (
              <div className="text-muted small mt-2">
                <i className="fa fa-spinner fa-spin"></i> Buscando...
              </div>
            )}
          </Form.Group>

          {searchResults.length > 0 &&
            !selectedPoint &&
            !rightClickCoordinates && (
              <div className="search-results">
                <div className="mb-2">
                  <small className="text-muted">
                    Se encontraron {searchResults.length} resultado
                    {searchResults.length > 1 ? "s" : ""}
                  </small>
                </div>
                <div className="search-results-list">
                  {searchResults.map((result, idx) => {
                    const isSelected =
                      selectedPoint &&
                      Number(selectedPoint.latitude) ===
                        Number(result.latitude) &&
                      Number(selectedPoint.longitude) ===
                        Number(result.longitude);

                    return (
                      <div
                        key={idx}
                        className={`search-result-card ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => handleSelectResult(result)}
                      >
                        <div className="search-result-header">
                          <div className="search-result-indicator"></div>
                          <span className="search-result-index">
                            Opción {idx + 1}
                          </span>
                        </div>
                        <div className="search-result-body">
                          <div className="search-result-address">
                            {result.direccion || "Sin dirección"}
                          </div>
                          <div className="search-result-coords">
                            <small>
                              {Number(result.latitude)},{" "}
                              {Number(result.longitude)}
                            </small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Mostrar coordenadas del clic derecho si existen y no hay punto seleccionado */}
          {rightClickCoordinates && !selectedPoint && (
            <div className="right-click-coordinates-display mt-3">
              <h6 className="fw-bold mb-2">Coordenadas seleccionadas:</h6>
              <div className="right-click-coordinates-card">
                <div className="selected-point-body">
                  <div className="selected-point-address">
                    {rightClickCoordinates.direccion || "Sin dirección"}
                  </div>
                  <div className="selected-point-coords">
                    <small>
                      {Number(rightClickCoordinates.latitude)},{" "}
                      {Number(rightClickCoordinates.longitude)}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-2"
                  onClick={() => {
                    if (onClearRightClickCoordinates) {
                      onClearRightClickCoordinates();
                    }
                    setSearchValue("");
                    if (onSearchValueChange) {
                      onSearchValueChange("");
                    }
                  }}
                >
                  <i className="fa fa-times"></i> Limpiar coordenadas
                </button>
              </div>
            </div>
          )}

          {selectedPoint && (
            <div className="selected-point-display mt-3">
              <h6 className="fw-bold mb-2">Punto base seleccionado:</h6>
              <div className="selected-point-card">
                <div className="selected-point-body">
                  <div className="selected-point-address">
                    {selectedPoint.direccion || "Sin dirección"}
                  </div>
                  {selectedPoint.referencia && (
                    <div className="selected-point-reference">
                      <small className="text-muted">
                        Ref: {selectedPoint.referencia}
                      </small>
                    </div>
                  )}
                  <div className="selected-point-coords">
                    <small>
                      {Number(selectedPoint.latitude)},{" "}
                      {Number(selectedPoint.longitude)}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-2"
                  onClick={() => {
                    onSelectPoint(null);
                    if (onClearRightClickCoordinates) {
                      onClearRightClickCoordinates();
                    }
                    setSearchValue("");
                    if (onSearchValueChange) {
                      onSearchValueChange("");
                    }
                  }}
                >
                  <i className="fa fa-times"></i> Limpiar selección
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BasePointSearch;
